import { ScannerOverlay } from '@/components/scanner-overlay';
import { QueryClientProvider } from '@tanstack/react-query';
import { Link, Outlet, createRootRoute } from '@tanstack/react-router';
import { ThemeProvider } from 'next-themes';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Toaster } from '@/components/ui/sonner';
import { buttonVariants } from '@/components/ui/button';
import { RouteErrorBoundary as SharedRouteErrorBoundary } from '@/components/shared/RouteErrorBoundary';
import { useLocale } from '@/hooks/use-locale';
import { queryClient } from '@/lib/query-client';

function RootLayout() {
  const { locale, direction } = useLocale();

  // Keeps <html lang>/<html dir> in step with the active locale for the whole
  // session, including after a hot reload.
  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = direction;
  }, [locale, direction]);

  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="light"
      enableSystem
      storageKey="app_theme"
      disableTransitionOnChange
    >
      <QueryClientProvider client={queryClient}>
        <Outlet />
        <ScannerOverlay />
        <Toaster position={direction === 'rtl' ? 'top-left' : 'top-right'} dir={direction} />
      </QueryClientProvider>
    </ThemeProvider>
  );
}

/**
 * The last stop for an error that escaped every route (rule CR1/CR2: the
 * `_app` layout and each top-level feature route get their own instance of
 * `RouteErrorBoundary` too, scoped to a re-mount rather than this one's full
 * reload — see that component for why). `min-h-screen` here is this
 * boundary's own choice as the outermost, full-page instance; a nested one
 * passes a shorter `className` so it does not force full-viewport height
 * inside a smaller content area (rule CR2's "leaves the navigation sidebar
 * ... intact").
 */
function RootErrorBoundary({ error }: { error: unknown }) {
  return <SharedRouteErrorBoundary error={error} boundary="root" className="min-h-screen" />;
}

/**
 * Rule E4 (case b): a client-side route the router itself cannot match —
 * a different mechanism entirely from an API `NOT_FOUND` (case a, handled by
 * `useApiError()` elsewhere), but owed the same standard this file's own
 * `RouteErrorBoundary` already meets: a translated message and a real
 * recovery action, never a bare, untranslated "404".
 */
function RouteNotFound() {
  const { t } = useTranslation(['errors', 'common']);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center">
      <p className="text-lg font-medium">{t('errors:routeNotFound')}</p>
      <Link to="/" className={buttonVariants()}>
        {t('common:nav.dashboard')}
      </Link>
    </div>
  );
}

export const Route = createRootRoute({
  component: RootLayout,
  errorComponent: RootErrorBoundary,
  notFoundComponent: RouteNotFound,
});
